import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { MovementDataPoint, MovementMetric, MovementTrend, DetectedChange } from './interfaces/types';

interface TrendVisualizerProps {
  trend: MovementTrend;
  metric: MovementMetric;
  change?: DetectedChange;
  showProjection?: boolean;
}

const TrendVisualizer: React.FC<TrendVisualizerProps> = ({ 
  trend, 
  metric, 
  change,
  showProjection = false
}) => {
  // Format timestamps for display
  const chartData = trend.values.map(dataPoint => ({
    timestamp: dataPoint.timestamp,
    date: dataPoint.timestamp.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    value: dataPoint.value
  }));
  
  // Calculate trend line for visualization
  const firstPoint = trend.values[0];
  const lastPoint = trend.values[trend.values.length - 1];
  
  // If we have at least 2 data points, add a trend line
  let trendLineData: {date: string; timestamp: Date; trendValue: number}[] = [];
  if (trend.values.length >= 2 && firstPoint && lastPoint) {
    const startTime = firstPoint.timestamp.getTime();
    const endTime = lastPoint.timestamp.getTime();
    const startValue = firstPoint.value;
    
    // Get trend line points
    trendLineData = trend.values.map(point => {
      const daysDiff = (point.timestamp.getTime() - startTime) / (24 * 60 * 60 * 1000);
      const trendValue = startValue + (trend.slope * daysDiff);
      return {
        date: point.timestamp.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        timestamp: point.timestamp,
        trendValue
      };
    });
    
    // Add projection points if requested
    if (showProjection) {
      const projectionDays = 30; // Project 30 days forward
      const interval = (endTime - startTime) / (trend.values.length - 1);
      
      for (let i = 1; i <= 5; i++) { // Add 5 projection points
        const projTime = new Date(endTime + (interval * i));
        const daysDiff = (projTime.getTime() - startTime) / (24 * 60 * 60 * 1000);
        const projValue = startValue + (trend.slope * daysDiff);
        
        trendLineData.push({
          date: projTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          timestamp: projTime,
          trendValue: projValue
        });
      }
    }
  }
  
  // Determine the color to use for the trend line based on the change direction
  const getTrendColor = () => {
    if (!change) return '#8884d8'; // Default color if no change is provided
    
    switch (change.direction) {
      case 'IMPROVING':
        return '#4caf50'; // Green for improvement
      case 'DECLINING':
        return '#f44336'; // Red for decline
      case 'FLUCTUATING':
        return '#ff9800'; // Orange for fluctuation
      default:
        return '#8884d8'; // Purple for unknown or all others
    }
  };
  
  // Determine min and max values for Y-axis with some padding
  const allValues = [...trend.values.map(p => p.value), ...trendLineData.map(p => p.trendValue)];
  const minValue = Math.min(...allValues) * 0.95; // Add 5% padding
  const maxValue = Math.max(...allValues) * 1.05; // Add 5% padding
  
  // Format tooltip data
  const renderTooltip = (props: any) => {
    const { payload, label } = props;
    if (!payload || payload.length === 0) return null;
    
    return (
      <div className="bg-white p-2 border rounded shadow-sm">
        <p className="font-medium">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={`tooltip-${index}`} style={{ color: entry.color }}>
            {entry.name}: {entry.value.toFixed(2)} {metric.unit}
          </p>
        ))}
      </div>
    );
  };
  
  // Add a reference line for the normal range if available
  const normalRangeLines = metric.normalRange ? [
    <ReferenceLine key="min" y={metric.normalRange.min} stroke="#b3b3b3" strokeDasharray="3 3" />,
    <ReferenceLine key="max" y={metric.normalRange.max} stroke="#b3b3b3" strokeDasharray="3 3" />
  ] : [];
  
  return (
    <div className="w-full h-64 mt-4">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            interval="preserveStartEnd" 
          />
          <YAxis 
            domain={[minValue, maxValue]} 
            tick={{ fontSize: 12 }}
            label={{ 
              value: metric.unit, 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle' }
            }}
          />
          <Tooltip content={renderTooltip} />
          <Legend />
          
          {/* Actual data values */}
          <Line 
            type="monotone" 
            dataKey="value" 
            name={metric.name} 
            stroke="#8884d8" 
            activeDot={{ r: 6 }} 
            dot={{ stroke: '#8884d8', strokeWidth: 2, r: 3 }}
          />
          
          {/* Trend line */}
          {trendLineData.length > 0 && (
            <Line 
              data={trendLineData} 
              type="linear"
              dataKey="trendValue" 
              name="Trend" 
              stroke={getTrendColor()} 
              strokeDasharray="5 5"
              dot={false}
            />
          )}
          
          {/* Normal range reference lines */}
          {normalRangeLines}
          
          {/* Add a marker for the midpoint date if we have a change */}
          {change && (
            <ReferenceLine 
              x={change.datePeriod.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              stroke="#aaa"
              strokeDasharray="3 3"
              label={{ value: 'Analysis Start', position: 'insideTopRight', fontSize: 10 }}
            />
          )}
          
          {/* Add a projection indicator if showing projections */}
          {showProjection && lastPoint && (
            <ReferenceLine 
              x={lastPoint.timestamp.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              stroke="#444"
              strokeDasharray="5 5"
              label={{ value: 'Projection', position: 'insideTopRight', fontSize: 10 }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TrendVisualizer; 